import { Effect, Fiber } from "effect";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useSyncExternalStore,
  type ReactNode,
} from "react";
import * as Sentry from "@sentry/react";
import { ArrowUpCircle, RefreshCw, X } from "react-feather";
import { useTranslation } from "@/hooks/use-translations";
import { executingReleaseId } from "@/lib/deployment/build-identity";
import { VersionCheckContext } from "@/hooks/use-version-check";
import { subscribeRequestFailures } from "@/lib/deployment/request-failures";
import { makeBrowserRefresh } from "@/lib/deployment/refresh";
import { readDeployedVersion } from "@/lib/deployment/deployed-version";
import { makeUpdateNotice } from "@/lib/deployment/update-notice";

type UpdateNotice = ReturnType<typeof makeUpdateNotice>;

/** The banner occupies its own row above the application. */
function RefreshNotice({ notice }: { notice: UpdateNotice }) {
  const { t } = useTranslation();
  const { visible, preparing, failed } = useSyncExternalStore(
    notice.subscribe,
    notice.getSnapshot,
  );
  const task = useRef<Fiber.RuntimeFiber<void, unknown> | null>(null);
  useEffect(
    () => () => {
      if (task.current) Effect.runFork(Fiber.interrupt(task.current));
    },
    [],
  );
  if (!visible) return null;
  return (
    <div
      data-testid="version-mismatch-toast"
      role="status"
      aria-live="polite"
      className="shrink-0 border-b border-solid border-slate-200 bg-slate-50 px-4 py-2 text-slate-900"
    >
      <div className="mx-auto flex w-full max-w-screen-xl items-center gap-3 sm:justify-center">
        <span
          aria-hidden="true"
          className="hidden h-8 w-8 shrink-0 items-center justify-center rounded-full bg-blue-100 text-blue-700 sm:flex"
        >
          <ArrowUpCircle size={18} />
        </span>
        <div className="min-w-0 flex-1 sm:flex sm:flex-initial sm:items-baseline sm:gap-2">
          <p className="m-0 text-xs font-semibold leading-5">
            {t("appUpdate.title")}
          </p>
          <p className="m-0 text-[13px] leading-5 text-slate-600">
            {t(failed ? "appUpdate.unavailable" : "appUpdate.advice")}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          <button
            data-testid="version-mismatch-refresh"
            type="button"
            disabled={preparing}
            aria-busy={preparing}
            className="inline-flex min-h-11 items-center justify-center gap-2 whitespace-nowrap rounded-full border-0 bg-base-black px-3 py-2 text-xs font-semibold leading-5 text-white transition-colors hover:bg-slate-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-600 disabled:cursor-wait disabled:opacity-60"
            onClick={() => {
              task.current = Effect.runFork(notice.refresh);
            }}
          >
            <RefreshCw
              size={16}
              aria-hidden="true"
              className={preparing ? "animate-spin" : ""}
            />
            {t("appUpdate.refresh")}
          </button>
          <button
            data-testid="version-mismatch-dismiss"
            type="button"
            aria-label={t("common.close")}
            className="inline-flex h-11 w-11 items-center justify-center rounded-full border-0 bg-transparent p-0 text-slate-500 transition-colors hover:bg-slate-200 hover:text-slate-900 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-600"
            onClick={notice.dismiss}
          >
            <X size={18} aria-hidden="true" />
          </button>
        </div>
      </div>
    </div>
  );
}

/** One provider for every route, with explicit checks and browser-focus checks. */
export function VersionCheckProvider({
  children,
  browserRelease = executingReleaseId,
  readRelease = readDeployedVersion,
}: {
  children?: ReactNode;
  browserRelease?: string | null;
  readRelease?: typeof readDeployedVersion;
}) {
  const notice = useMemo(
    () =>
      makeUpdateNotice({
        browserRelease,
        readRelease,
        refresh: makeBrowserRefresh({
          location: window.location,
          navigator,
          fetch: window.fetch.bind(window),
        }),
        reportDefect: (error) => Sentry.captureException(error),
      }),
    [browserRelease, readRelease],
  );
  const check = useRef<Fiber.RuntimeFiber<void, never> | null>(null);
  const requestVersionCheck = useCallback(() => {
    // Keep the live fiber for disposal; another caller shares the in-flight check.
    if (notice.checking()) return;
    check.current = Effect.runFork(notice.check);
  }, [notice]);
  const context = useMemo(
    () => ({ requestVersionCheck }),
    [requestVersionCheck],
  );
  useEffect(() => {
    const onFocus = () => {
      if (document.visibilityState === "visible") requestVersionCheck();
    };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onFocus);
    const unsubscribe = subscribeRequestFailures(requestVersionCheck);
    requestVersionCheck();
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onFocus);
      unsubscribe();
      if (check.current) Effect.runFork(Fiber.interrupt(check.current));
    };
  }, [requestVersionCheck]);
  return (
    <VersionCheckContext.Provider value={context}>
      <div className="flex h-dvh flex-col">
        <RefreshNotice notice={notice} />
        <div className="min-h-0 flex-1 overflow-auto" data-testid="app-content">
          {children}
        </div>
      </div>
    </VersionCheckContext.Provider>
  );
}
